/**
 * التقاط هويّة جلسة GA4 (`client_id` + `session_id`) بالطريقة الرسميّة فقط:
 * `gtag('get', MEASUREMENT_ID, field, callback)`.
 *
 * القواعد الصارمة:
 *  • لا تلفيق معرّفات ولا تحليل يدويّ لكوكي `_ga`/`_ga_XXXX`.
 *  • موافقة التحليلات شرط قبل الالتقاط، وتُعاد الفحص بعد انتظار الاستدعاء.
 *  • مهلة محدودة — التحليلات اختياريّة ولا يجوز أن تُعطّل الدفع.
 *  • القيمة الملتقطة تبقى في الذاكرة فقط (لا تخزين قبل/بعد الموافقة).
 *
 * لماذا: الدفع يقع على checkout شوبيفاي، فالجسر الوحيد بين جلسة المتصفّح
 * وحدث الشراء الموقَّع (webhook) هو تمرير هذه الهويّة في سمات السلّة.
 */

import { GA4_MEASUREMENT_ID } from "./ga4-config";
import { hasAdsConsent, hasAnalyticsConsent } from "./cookie-consent";
import { ensureGoogleTagLoaded, isGoogleTagReady, onGoogleTagReady } from "./googleTagBootstrap";

export interface GaSessionSnapshot {
  clientId: string;
  sessionId: string;
  /** ISO لوقت الالتقاط — الخادم يستخدمه للتحقّق من عمر الجلسة */
  capturedAt: string;
  analytics: boolean;
  ads: boolean;
}

export const GA_SESSION_ATTR_KEYS = [
  "_ga_client_id",
  "_ga_session_id",
  "_ga_captured_at",
  "_ga_consent",
] as const;

const DEFAULT_TIMEOUT_MS = 1_500;

/** client_id الرسميّ بصيغة `<random>.<timestamp>` */
const CLIENT_ID_RE = /^\d{1,20}\.\d{6,20}$/;
/** session_id هو timestamp بالثواني */
const SESSION_ID_RE = /^\d{9,13}$/;

function gtagFn(): ((...args: unknown[]) => void) | null {
  if (typeof window === "undefined") return null;
  const fn = (window as typeof window & { gtag?: (...a: unknown[]) => void }).gtag;
  return typeof fn === "function" ? fn : null;
}

/** انتظار جاهزيّة الوسم بمهلة — لا polling. */
function waitForTag(timeoutMs: number): Promise<boolean> {
  if (isGoogleTagReady()) return Promise.resolve(true);
  return new Promise((resolve) => {
    let done = false;
    const finish = (ok: boolean) => {
      if (done) return;
      done = true;
      off();
      window.clearTimeout(timer);
      resolve(ok);
    };
    const off = onGoogleTagReady(() => finish(true));
    const timer = window.setTimeout(() => finish(false), timeoutMs);
  });
}

function getField(field: "client_id" | "session_id", timeoutMs: number): Promise<string | null> {
  const gtag = gtagFn();
  if (!gtag) return Promise.resolve(null);
  return new Promise((resolve) => {
    let done = false;
    const finish = (value: string | null) => {
      if (done) return;
      done = true;
      window.clearTimeout(timer);
      resolve(value);
    };
    const timer = window.setTimeout(() => finish(null), timeoutMs);
    try {
      gtag("get", GA4_MEASUREMENT_ID, field, (value: unknown) => {
        finish(typeof value === "string" || typeof value === "number" ? String(value) : null);
      });
    } catch {
      finish(null);
    }
  });
}

/**
 * يلتقط هويّة الجلسة، أو `null` إن غابت الموافقة أو لم يجب الوسم في المهلة.
 * لا يرمي أبداً.
 */
export async function captureGaSession(opts?: { timeoutMs?: number }): Promise<GaSessionSnapshot | null> {
  if (typeof window === "undefined") return null;
  if (!hasAnalyticsConsent()) return null;
  const budget = opts?.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const startedAt = Date.now();
  try {
    ensureGoogleTagLoaded();
    const ready = await waitForTag(budget);
    if (!ready) return null;
    const remaining = Math.max(200, budget - (Date.now() - startedAt));
    const clientId = await getField("client_id", remaining);
    const sessionId = await getField("session_id", Math.max(200, budget - (Date.now() - startedAt)));
    // الموافقة قد تُسحب أثناء الانتظار — نُعيد الفحص قبل الاستخدام.
    if (!hasAnalyticsConsent()) return null;
    if (!clientId || !CLIENT_ID_RE.test(clientId)) return null;
    if (!sessionId || !SESSION_ID_RE.test(sessionId)) return null;
    const snapshot: GaSessionSnapshot = {
      clientId,
      sessionId,
      capturedAt: new Date().toISOString(),
      analytics: true,
      ads: hasAdsConsent(),
    };
    cached = snapshot;
    return snapshot;
  } catch {
    return null;
  }
}

/**
 * ذاكرة الجلسة (في الذاكرة فقط) + تسخين في الخلفيّة.
 *
 * لماذا: إضافة للسلّة والذهاب للدفع كانا ينتظران الوسم حتّى ١٫٥ ثانية، فتتجمّد
 * التجربة كلّما كان سكربت جوجل بطيئاً أو محجوباً. الشراء لا يجوز أن ينتظر
 * التحليلات: نقرأ آخر لقطة حقيقيّة فوراً، ونُسخّن اللقطة في الخلفيّة. لا
 * تلفيق معرّفات: بلا لقطة حقيقيّة لا سمات جلسة.
 */
const CACHE_TTL_MS = 30 * 60_000;
let cached: GaSessionSnapshot | null = null;
let inFlight: Promise<GaSessionSnapshot | null> | null = null;

function cacheAgeMs(): number {
  if (!cached) return Infinity;
  const ms = Date.parse(cached.capturedAt);
  return Number.isFinite(ms) ? Date.now() - ms : Infinity;
}

/** يبدأ التقاطاً في الخلفيّة (بلا انتظار) إن لم تكن هناك لقطة حديثة. */
export function primeGaSession(opts?: { timeoutMs?: number }): void {
  if (typeof window === "undefined") return;
  if (!hasAnalyticsConsent()) return;
  if (inFlight || cacheAgeMs() < CACHE_TTL_MS) return;
  inFlight = captureGaSession(opts)
    .catch(() => null)
    .finally(() => {
      inFlight = null;
    }) as Promise<GaSessionSnapshot | null>;
}

/**
 * آخر لقطة صالحة بلا أيّ انتظار. تُعاد `null` عند سحب موافقة التحليلات أو
 * قِدَم اللقطة. علم الإعلانات يُعاد حسابه الآن (الموافقة قد تتغيّر).
 */
export function getCachedGaSession(): GaSessionSnapshot | null {
  if (typeof window === "undefined") return null;
  if (!hasAnalyticsConsent()) {
    cached = null;
    return null;
  }
  if (!cached || cacheAgeMs() >= CACHE_TTL_MS) return null;
  return { ...cached, ads: hasAdsConsent() };
}

/** اختبارات فقط. */
export function __resetGaSessionCacheForTests(): void {
  cached = null;
  inFlight = null;
}

/** سمات السلّة الحاملة لهويّة الجلسة (بادئة `_` = مخفيّة عن العميل). */
export function buildGaSessionAttributes(
  snapshot: GaSessionSnapshot | null,
): Array<{ key: string; value: string }> {
  if (!snapshot) return [];
  const consent = [snapshot.analytics ? "analytics" : null, snapshot.ads ? "ads" : null]
    .filter(Boolean)
    .join(",");
  return [
    { key: "_ga_client_id", value: snapshot.clientId },
    { key: "_ga_session_id", value: snapshot.sessionId },
    { key: "_ga_captured_at", value: snapshot.capturedAt },
    { key: "_ga_consent", value: consent },
  ];
}
